import { createClient } from '@supabase/supabase-js'
import CryptoJS from 'crypto-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// SHA256 hash function
const hashPassword = (password) => {
  return CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex)
}

// Helper functions for database operations
export const contactsAPI = {
  // Insert new contact
  async create(contactData) {
    const { data, error } = await supabase
      .from('contacts')
      .insert([contactData])
      .select()
    
    if (error) throw error
    return data[0]
  },

  // Get all contacts
  async getAll() {
    const { data, error } = await supabase
      .from('contacts')
      .select('*')
      .order('created_at', { ascending: false })
    
    if (error) throw error
    return data
  },

  // Update contact status
  async updateStatus(id, status) {
    const { data, error } = await supabase
      .from('contacts')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
    
    if (error) throw error
    return data[0]
  },

  // Delete contact
  async delete(id) {
    const { error } = await supabase
      .from('contacts')
      .delete()
      .eq('id', id)
    
    if (error) throw error
    return true
  },

  // Subscribe to real-time changes
  subscribe(callback) {
    return supabase
      .channel('contacts')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'contacts' },
        callback
      )
      .subscribe()
  }
}

// Helper functions for blogs operations
export const blogsAPI = {
  // Insert new blog
  async create(blogData) {
    // Filter out undefined/null values and handle optional image field
    const cleanData = {};
    if (blogData.title) cleanData.title = blogData.title;
    if (blogData.description) cleanData.description = blogData.description;
    if (blogData.publish_date) cleanData.publish_date = blogData.publish_date;
    if (blogData.content) cleanData.content = blogData.content;
    // Only include image if it exists and is not empty
    if (blogData.image && blogData.image.trim() !== '') {
      cleanData.image = blogData.image;
    }
    
    const { data, error } = await supabase
      .from('blogs')
      .insert([cleanData])
      .select()
    
    if (error) throw error
    return data[0]
  },

  // Get all blogs
  async getAll() {
    const { data, error } = await supabase
      .from('blogs')
      .select('*')
      .order('created_at', { ascending: false })
    
    if (error) throw error
    return data
  },

  // Get blog by ID
  async getById(id) {
    const { data, error } = await supabase
      .from('blogs')
      .select('*')
      .eq('id', id)
      .single()
    
    if (error) throw error
    return data
  },

  // Update blog
  async update(id, blogData) {
    // Filter out undefined/null values and handle optional image field
    const cleanData = {};
    if (blogData.title) cleanData.title = blogData.title;
    if (blogData.description) cleanData.description = blogData.description;
    if (blogData.publish_date) cleanData.publish_date = blogData.publish_date;
    if (blogData.content) cleanData.content = blogData.content;
    // Only include image if it exists and is not empty
    if (blogData.image && blogData.image.trim() !== '') {
      cleanData.image = blogData.image;
    }
    cleanData.updated_at = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('blogs')
      .update(cleanData)
      .eq('id', id)
      .select()
    
    if (error) throw error
    return data[0]
  },

  // Delete blog
  async delete(id) {
    const { error } = await supabase
      .from('blogs')
      .delete()
      .eq('id', id)
    
    if (error) throw error
    return true
  },

  // Subscribe to real-time changes
  subscribe(callback) {
    return supabase
      .channel('blogs')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'blogs' },
        callback
      )
      .subscribe()
  }
}
// Admin authentication API
export const adminAuthAPI = {
  // Authenticate admin user with database
  async authenticate(username, password) {
    try {
      // Hash the provided password
      const passwordHash = hashPassword(password)
      
      // Query database for user with matching username and password hash
      const { data, error } = await supabase
        .from('admin_users')
        .select('id, username, email, full_name, role, is_active, last_login')
        .eq('username', username)
        .eq('password_hash', passwordHash)
        .eq('is_active', true)
        .single()
      
      if (error) {
        if (error.code === 'PGRST116') {
          // No matching user found
          throw new Error('Invalid username or password')
        }
        throw error
      }
      
      // Update last login time
      await supabase
        .from('admin_users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.id)
      
      return {
        success: true,
        user: data,
        message: 'Login successful!'
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Authentication failed'
      }
    }
  },

  // Get admin user by ID
  async getById(id) {
    const { data, error } = await supabase
      .from('admin_users')
      .select('id, username, email, full_name, role, is_active, last_login, created_at')
      .eq('id', id)
      .single()
    
    if (error) throw error
    return data
  },

  // Update admin user password
  async updatePassword(id, newPassword) {
    const passwordHash = hashPassword(newPassword)
    
    const { data, error } = await supabase
      .from('admin_users')
      .update({ 
        password_hash: passwordHash,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
    
    if (error) throw error
    return data[0]
  },

  // Create new admin user
  async create(userData) {
    const passwordHash = hashPassword(userData.password)
    
    const cleanData = {
      username: userData.username,
      password_hash: passwordHash,
      email: userData.email,
      full_name: userData.full_name,
      role: userData.role || 'admin'
    }
    
    const { data, error } = await supabase
      .from('admin_users')
      .insert([cleanData])
      .select('id, username, email, full_name, role, is_active, created_at')
    
    if (error) throw error
    return data[0]
  }
}